import { CircleCheck, Download, Eye, Plus } from "lucide-react";
import { useMemo, useState, type FC } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { formatDate, initialsOf, isSoon } from "../../companies/utils";
import { PortalAPI } from "../api/portalApi";
import { EmployeeSheet } from "../components/EmployeeSheet";
import { NeedsYouCard } from "../components/NeedsYouCard";
import { usePortal } from "../components/PortalShell";
import {
  filterEmployees,
  greeting,
  requestStatusText,
  waitingLabel
} from "../portalModel";
import { usePortalHome } from "../queries";
import { PORTAL_REQUEST_PATH } from "../routes";
import type {
  PortalEmployee,
  PortalMe,
  PortalRequest,
  WaitingOn
} from "../types";

const ALL = "__all__";
const COLS =
  "grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.1fr)_110px_140px_130px_100px] items-center gap-3 px-4";

const WAITING: WaitingOn[] = ["company", "employee", "firm", "uscis", "nobody"];

const downloadCsv = async (companyName: string) => {
  try {
    const csv = await PortalAPI.employeesCsv();
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${companyName} employees.csv`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    toast.error(error instanceof Error ? error.message : "Couldn't export");
  }
};

const who = (name: string | undefined, me: PortalMe) =>
  name === me.contact.name ? "you" : name;

const RequestRow: FC<{ r: PortalRequest; me: PortalMe; last: boolean }> = ({
  r,
  me,
  last
}) => (
  <div
    className={cn(
      "grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_220px] items-center gap-3 px-4 py-2.5",
      !last && "border-b border-border"
    )}
    data-testid={`portal-request-${r.id}`}
  >
    <span className="flex min-w-0 flex-col">
      <span className="truncate text-base font-medium">
        {r.employee_name} · {r.estimate.label || r.case_type}
      </span>
      <span className="truncate text-xs text-muted-foreground">
        {[r.job_title, r.cost_center].filter(Boolean).join(" · ")}
      </span>
    </span>
    <span className="text-sm text-muted-foreground">
      Requested by {who(r.requested_by.name, me)}
      {r.created_at ? ` ${formatDate(r.created_at)}` : ""}
      {r.approved_by ? ` · approved by ${who(r.approved_by.name, me)}` : ""}
      {r.decline_reason ? ` · “${r.decline_reason}”` : ""}
    </span>
    <span className="flex justify-end">
      <Badge
        variant="secondary"
        className={cn(
          "font-normal",
          r.status === "accepted" && "bg-success-soft text-success",
          (r.status === "pending_firm" || r.status === "pending_approval") &&
            "bg-warn-soft text-warn"
        )}
      >
        {requestStatusText(r, me.firm.name)}
      </Badge>
    </span>
  </div>
);

const EmployeeTable: FC<{ rows: PortalEmployee[]; me: PortalMe }> = ({
  rows,
  me
}) => {
  const [center, setCenter] = useState<string | null>(null);
  const [waiting, setWaiting] = useState<WaitingOn | null>(null);
  const [open, setOpen] = useState<PortalEmployee | null>(null);
  const centers = useMemo(
    () => [...new Set(rows.map((r) => r.cost_center).filter(Boolean))].sort(),
    [rows]
  );
  const shown = filterEmployees(rows, center, waiting);
  const canOpen = me.features.docs || me.features.messages;
  return (
    <div className="flex flex-col gap-2">
      <span className="flex items-center gap-2">
        <span className="text-base font-semibold">Employees</span>
        <span className="flex-1" />
        {centers.length ? (
          <Select
            value={center ?? ALL}
            onValueChange={(v) => setCenter(v === ALL ? null : v)}
          >
            <SelectTrigger
              size="sm"
              className="h-7 w-37.5 text-xs"
              aria-label="Cost center"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All Cost Centers</SelectItem>
              {centers.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        <Select
          value={waiting ?? ALL}
          onValueChange={(v) => setWaiting(v === ALL ? null : (v as WaitingOn))}
        >
          <SelectTrigger
            size="sm"
            className="h-7 w-37.5 text-xs"
            aria-label="Waiting on"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Waiting on Anyone</SelectItem>
            {WAITING.filter((w) => w !== "nobody").map((w) => (
              <SelectItem key={w} value={w}>
                Waiting on {waitingLabel(w, me.firm.name)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="xs"
          variant="outline"
          onClick={() => void downloadCsv(me.company.name)}
        >
          <Download /> Export
        </Button>
      </span>
      <div
        className="rounded-lg border border-border bg-background"
        role="table"
      >
        <div
          className={cn(
            COLS,
            "h-9 border-b border-border text-xs text-muted-foreground"
          )}
          role="row"
        >
          <span>Employee</span>
          <span>Case</span>
          <span>Status</span>
          <span>Next date</span>
          <span>Waiting on</span>
          <span>Cost center</span>
        </div>
        {shown.length ? (
          shown.map((e, i) => (
            <button
              key={e.individual_id}
              type="button"
              role="row"
              disabled={!canOpen}
              onClick={() => setOpen(e)}
              data-testid={`portal-employee-${e.individual_id}`}
              className={cn(
                COLS,
                "h-13 w-full text-left enabled:hover:bg-fill-1",
                i < shown.length - 1 && "border-b border-border"
              )}
            >
              <span className="flex min-w-0 items-center gap-2.5">
                <span className="flex size-6.5 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-semibold">
                  {initialsOf(e.name)}
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-base font-medium">
                    {e.name}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {e.dependent_of
                      ? `Dependent of ${
                          rows.find((r) => r.individual_id === e.dependent_of)
                            ?.name ?? "an employee"
                        }`
                      : e.job || e.entity || ""}
                  </span>
                </span>
              </span>
              <span className="truncate text-base">{e.case_label}</span>
              <span>
                <Badge variant="secondary" className="font-normal">
                  {e.status}
                </Badge>
              </span>
              <span className="flex flex-col leading-4.25">
                <span className="truncate text-xs text-muted-foreground">
                  {e.next_date?.title ?? "–"}
                </span>
                {e.next_date ? (
                  <span
                    className={cn(
                      "text-sm",
                      isSoon(e.next_date) && "text-warn"
                    )}
                  >
                    {formatDate(e.next_date.due_date)}
                  </span>
                ) : null}
              </span>
              <span>
                <Badge
                  variant="secondary"
                  className={cn(
                    "font-normal",
                    e.waiting_on === "company" && "bg-warn-soft text-warn"
                  )}
                >
                  {waitingLabel(e.waiting_on, me.firm.name)}
                </Badge>
              </span>
              <span className="truncate font-mono text-xs">
                {e.cost_center}
              </span>
            </button>
          ))
        ) : (
          <div className="px-4 py-6 text-center text-base text-muted-foreground">
            {rows.length
              ? "No employees match these filters."
              : `No employees with ${me.firm.name} yet.`}
          </div>
        )}
      </div>
      {me.contact.access === "requested" ? (
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Eye className="size-3.5" />
          You see the employees you requested. Ask {me.firm.name} if you need to
          see more.
        </span>
      ) : null}
      <EmployeeSheet employee={open} me={me} onClose={() => setOpen(null)} />
    </div>
  );
};

export const PortalEmployeesPage: FC = () => {
  const me = usePortal();
  const { data, isLoading } = usePortalHome();
  if (isLoading || !data) return <Skeleton className="h-64" />;
  const { counts } = data;
  return (
    <>
      <span className="flex items-end gap-3">
        <span className="flex flex-col gap-1">
          <span className="text-xl font-semibold">
            {greeting(me.contact.name)}
          </span>
          {me.features.employees ? (
            <span className="text-base text-muted-foreground">
              {counts.employees}{" "}
              {counts.employees === 1 ? "employee" : "employees"} with{" "}
              {me.firm.name} · {counts.open_cases} open{" "}
              {counts.open_cases === 1 ? "case" : "cases"} ·{" "}
              {counts.upcoming_90} {counts.upcoming_90 === 1 ? "date" : "dates"}{" "}
              in the next 90 days
            </span>
          ) : null}
        </span>
        <span className="flex-1" />
        {me.features.requests ? (
          <Button variant="primary" asChild>
            <Link to={PORTAL_REQUEST_PATH}>
              <Plus /> Request a case
            </Link>
          </Button>
        ) : null}
      </span>

      <div className="flex flex-col gap-2" data-testid="needs-you">
        <span className="text-base font-semibold">Needs you</span>
        {data.needs_you.length ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {data.needs_you.map((n) => (
              <NeedsYouCard key={n.id} item={n} />
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3.5 py-3 text-base text-success">
            <CircleCheck className="size-4" />
            Nothing needs you right now. We&apos;ll email you when something
            does.
          </div>
        )}
      </div>

      {data.requests.length ? (
        <div className="flex flex-col gap-2">
          <span className="text-base font-semibold">Requests</span>
          <div className="rounded-lg border border-border bg-background">
            {data.requests.map((r, i) => (
              <RequestRow
                key={r.id}
                r={r}
                me={me}
                last={i === data.requests.length - 1}
              />
            ))}
          </div>
        </div>
      ) : null}

      {me.features.employees ? (
        <EmployeeTable rows={data.employees} me={me} />
      ) : null}
    </>
  );
};
